import { ReplicateError, classifyHttpError } from './errors';
import {
  ReplicateFileSchema,
  ReplicatePredictionSchema,
  type ReplicateFile,
  type ReplicatePrediction,
} from './types';

const REPLICATE_BASE = '/replicate/v1';

export interface ReplicateClientOptions {
  token: string;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export class ReplicateClient {
  private readonly token: string;
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ReplicateClientOptions) {
    if (!opts.token) throw new ReplicateError('unauthorized', 'Replicate token required.');
    this.token = opts.token;
    this.baseUrl = opts.baseUrl ?? REPLICATE_BASE;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async request(
    path: string,
    init: RequestInit & { signal?: AbortSignal } = {},
  ): Promise<Response> {
    const url = path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Token ${this.token}`);
    if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    let resp: Response;
    try {
      resp = await this.fetchImpl(url, { ...init, headers });
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        throw new ReplicateError('aborted', 'Request aborted.', undefined, err);
      }
      const msg = (err as Error)?.message ?? '';
      const isCors = /CORS|Failed to fetch/i.test(msg);
      throw new ReplicateError(
        isCors ? 'cors' : 'network',
        `Network error: ${msg || 'unknown'}`,
        undefined,
        err,
      );
    }
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw classifyHttpError(resp.status, text);
    }
    return resp;
  }

  async createPrediction(input: {
    model: string;
    version?: string;
    input: Record<string, unknown>;
    signal?: AbortSignal;
  }): Promise<ReplicatePrediction> {
    const body = input.version
      ? { version: input.version, input: input.input }
      : { input: input.input };
    const path = input.version ? '/predictions' : `/models/${input.model}/predictions`;
    const resp = await this.request(path, {
      method: 'POST',
      body: JSON.stringify(body),
      ...(input.signal ? { signal: input.signal } : {}),
    });
    const json = (await resp.json()) as unknown;
    return ReplicatePredictionSchema.parse(json);
  }

  async getPrediction(id: string, signal?: AbortSignal): Promise<ReplicatePrediction> {
    const resp = await this.request(
      `/predictions/${id}`,
      signal ? { signal } : {},
    );
    const json = (await resp.json()) as unknown;
    return ReplicatePredictionSchema.parse(json);
  }

  async cancelPrediction(id: string): Promise<void> {
    await this.request(`/predictions/${id}/cancel`, { method: 'POST' });
  }

  async uploadFile(blob: Blob, filename: string, signal?: AbortSignal): Promise<ReplicateFile> {
    const form = new FormData();
    form.append('content', blob, filename);
    const resp = await this.request('/files', {
      method: 'POST',
      body: form,
      ...(signal ? { signal } : {}),
    });
    const json = (await resp.json()) as unknown;
    return ReplicateFileSchema.parse(json);
  }

  async testToken(): Promise<{ ok: boolean }> {
    // Cheapest endpoint: list account info.
    const resp = await this.request('/account');
    return { ok: resp.ok };
  }
}
